/* ============================================================
   LocalAiCatchup — data layer.

   The "Gaming-PC schlägt Rechenzentrum" chart is now fully
   DATA-DRIVEN: every dot is described as { date, index } and the
   pixel coordinates are computed from the scales below. Adding a
   model = adding one row to MODELS. The chart, area-fills, dots,
   labels, legend and the searchable picker all derive from here,
   so this scales from a handful of milestones up to the full
   Artificial-Analysis catalogue (≈540 models) without touching
   the component.

   NOTE: this is an illustrative marketing chart. It already mixes
   shipped models with near-term projections (Opus 4.8, Fable 5,
   Qwen3.6, DeepSeek V4 …) in the same spirit as the original deck.
   ============================================================ */

export type ProviderId =
  | 'openai' | 'gemini' | 'grok' | 'kimi'
  | 'qwen' | 'meta' | 'gemma'
  | 'mistral' | 'deepseek' | 'glm';

export type DeployMode = 'cloud' | 'local' | 'both';

/** Benchmark scores (AA Intelligence Index v4.1). All optional — not every model
 *  has every benchmark. Values are 0–100. */
export interface Bench { gpqa?: number; hle?: number; tbench?: number; }

export interface ModelPoint {
  id: string;
  provider: ProviderId;
  family: string;
  name: string;
  /** "YYYY-MM" — release (or projected release) month. Drives the x position. */
  date: string;
  /** Artificial Analysis Intelligence Index (v4.x). Drives the y position. */
  index: number;
  /** Curated milestone — added by the provider quick-chip and labelled on the chart. */
  key?: boolean;
  /** Headline model for the provider — gets the ★ marker + the benchmark panel. */
  star?: boolean;
  /** rough VRAM/params hint shown in the picker */
  size?: string;
  /** true = fits on a gaming PC (≤32 GB VRAM): small dense model or MoE with small active params */
  local?: boolean;
  /** benchmark scores */
  bench?: Bench;
  /** blended cost USD / 1M tokens (undefined or 0 = free local) */
  cost?: number;
  /** output speed (tokens/sec) */
  speed?: number;
  /** context window (K tokens) */
  context?: number;
}

export interface Provider {
  id: ProviderId;
  label: string;
  color: string;
  deployMode: DeployMode;
}

/** The "bar to beat" the whole chart compares against (Opus 4.5, ~6 months ago). */
export const REFERENCE = { name: 'Opus 4.5', index: 40.8, bench: { gpqa: 87, hle: 29, tbench: 50 } as Bench };

/* ── Scales ────────────────────────────────────────────────── */
export const VIEW = { w: 1000, h: 450 };
export const PLOT = { x0: 78, x1: 958, yBottom: 382, yTop: 44 };
export const I_MAX = 70;            // top of the index axis
const T_MAX = '2026-06';           // right edge ("now"); left edge is 2023-01 (months() origin)

const months = (d: string) => {
  const [y, m] = d.split('-').map(Number);
  return (y - 2023) * 12 + (m - 1);
};
const T_SPAN = months(T_MAX);

export const xOf = (date: string) =>
  PLOT.x0 + (months(date) / T_SPAN) * (PLOT.x1 - PLOT.x0);
export const yOf = (index: number) =>
  PLOT.yBottom - (Math.min(index, I_MAX) / I_MAX) * (PLOT.yBottom - PLOT.yTop);

/** dot radius scales subtly with recency/importance */
export const rOf = (m: ModelPoint) => (m.star ? 5 : m.key ? 3.8 : 2.8);

export const GRID_INDEX = [10, 20, 30, 40, 50, 60, 70];
export const YEAR_TICKS: { date: string; label: string }[] = [
  { date: '2023-01', label: "'23" },
  { date: '2024-01', label: "'24" },
  { date: '2025-01', label: "'25" },
  { date: '2026-01', label: "'26" },
  { date: '2026-06', label: 'now' },
];

/* ── Providers ─────────────────────────────────────────────── */
export const PROVIDERS: Provider[] = [
  { id: 'openai',   label: 'OpenAI',   color: '#10a37f', deployMode: 'cloud' },
  { id: 'gemini',   label: 'Gemini',   color: '#3b82f6', deployMode: 'cloud' },
  { id: 'grok',     label: 'Grok',     color: '#f59e0b', deployMode: 'cloud' },
  { id: 'kimi',     label: 'Kimi',     color: '#a855f7', deployMode: 'both' },
  { id: 'qwen',     label: 'Qwen',     color: '#4fffb0', deployMode: 'local' },
  { id: 'meta',     label: 'Meta',     color: '#818cf8', deployMode: 'local' },
  { id: 'gemma',    label: 'Gemma',    color: '#a3e635', deployMode: 'local' },
  { id: 'mistral',  label: 'Mistral',  color: '#f97316', deployMode: 'both' },
  { id: 'deepseek', label: 'DeepSeek', color: '#f43f5e', deployMode: 'local' },
  { id: 'glm',      label: 'GLM',      color: '#22d3ee', deployMode: 'local' },
];

export const PROVIDER_MAP: Record<ProviderId, Provider> =
  Object.fromEntries(PROVIDERS.map(p => [p.id, p])) as Record<ProviderId, Provider>;

/* ── Proprietary frontier (reference line, gold) ───────────────
   The running best of the closed flagships (OpenAI + Anthropic).
   Shown as the "data center keeps climbing" benchmark line. ── */
export interface FrontierPoint {
  name: string; date: string; index: number; maker: string;
  big?: boolean;
  noLabel?: boolean;
  labelDx?: number; labelDy?: number; anchor?: 'start' | 'middle' | 'end';
  bench?: Bench;
  cost?: number;
  speed?: number;
  context?: number;
}
export const FRONTIER: FrontierPoint[] = [
  { name: 'GPT-4',            date: '2023-03', index: 12,   maker: 'OpenAI',    labelDx: 8,   labelDy: -14, anchor: 'start' },
  { name: 'GPT-4o',           date: '2024-08', index: 21.8, maker: 'OpenAI',    labelDx: -10, labelDy: -16, anchor: 'end' },
  { name: 'Claude 3.5 Sonnet',date: '2024-11', index: 28,   maker: 'Anthropic', labelDx: 8,   labelDy: -14, anchor: 'start' },
  { name: 'GPT-5',            date: '2025-05', index: 36.1, maker: 'OpenAI',    labelDx: -10, labelDy: 20,  anchor: 'end' },
  { name: 'GPT-5.1 high',     date: '2025-09', index: 38.9, maker: 'OpenAI',    noLabel: true, bench: { gpqa: 85, hle: 25 }, cost: 11, speed: 88, context: 200 },
  { name: 'Claude Opus 4.5',  date: '2025-11', index: 40.8, maker: 'Anthropic', labelDx: -12, labelDy: -14, anchor: 'end', bench: { gpqa: 87, hle: 29, tbench: 50 }, cost: 10, context: 1000 },
  { name: 'Opus 4.6',         date: '2026-02', index: 43.7, maker: 'Anthropic', big: true, labelDx: -12, labelDy: -14, anchor: 'end', bench: { gpqa: 88, hle: 32, tbench: 58 }, cost: 10, speed: 55, context: 1000 },
  { name: 'GPT-5.5',          date: '2026-04', index: 54.8, maker: 'OpenAI',    big: true, labelDx: -12, labelDy: -14, anchor: 'end', bench: { gpqa: 94, hle: 44, tbench: 84 }, cost: 11.2, speed: 69, context: 922 },
  { name: 'Opus 4.7',         date: '2026-04', index: 53.5, maker: 'Anthropic', noLabel: true, bench: { gpqa: 91, hle: 40, tbench: 83 }, cost: 10, speed: 50, context: 1000 },
  { name: 'Opus 4.8',         date: '2026-05', index: 55.7, maker: 'Anthropic', big: true, labelDx: -8, labelDy: -14, anchor: 'end', bench: { gpqa: 92, hle: 46, tbench: 85 }, cost: 10, speed: 61, context: 1000 },
  { name: 'Claude Fable 5',   date: '2026-06', index: 59.9, maker: 'Anthropic', big: true, labelDx: -8, labelDy: -12, anchor: 'end', bench: { gpqa: 93, hle: 53, tbench: 85 }, cost: 20, context: 1000 },
];

/* ── Selectable provider models ────────────────────────────── */
export const MODELS: ModelPoint[] = [
  // ── OpenAI (cloud) ──
  { id: 'gpt-55-xh', provider: 'openai', family: 'GPT-5.5', name: 'GPT-5.5 (xhigh)', date: '2026-04', index: 54.8, key: true, star: true, bench: { gpqa: 94, hle: 44, tbench: 84 }, cost: 11.2, speed: 69, context: 922 },
  { id: 'gpt-55-hi', provider: 'openai', family: 'GPT-5.5', name: 'GPT-5.5 (high)', date: '2026-04', index: 53.1, key: true, bench: { gpqa: 93, hle: 43, tbench: 79 }, cost: 11.2, speed: 66, context: 922 },
  { id: 'gpt-55-md', provider: 'openai', family: 'GPT-5.5', name: 'GPT-5.5 (medium)', date: '2026-04', index: 50.4, bench: { gpqa: 93, hle: 41, tbench: 81 }, cost: 11.2, speed: 61, context: 922 },
  { id: 'gpt-54-min', provider: 'openai', family: 'GPT-5.4', name: 'GPT-5.4 mini', date: '2026-03', index: 40, key: true, bench: { gpqa: 87, hle: 27, tbench: 59 }, cost: 1.7, speed: 176, context: 400 },
  { id: 'gpt-54-nano', provider: 'openai', family: 'GPT-5.4', name: 'GPT-5.4 nano', date: '2026-03', index: 38.2, bench: { gpqa: 82, hle: 26, tbench: 61 }, cost: 0.5, speed: 174, context: 400 },
  { id: 'gpt-55-nr', provider: 'openai', family: 'GPT-5.5', name: 'GPT-5.5 (non-reasoning)', date: '2026-04', index: 35.4, bench: { gpqa: 77, hle: 13, tbench: 61 }, cost: 11.2, speed: 63, context: 922 },

  // ── Gemini (cloud) ──
  { id: 'gem-25pro',  provider: 'gemini', family: 'Gemini 2.5', name: 'Gemini 2.5 Pro', date: '2025-06', index: 27, key: true, bench: { gpqa: 84, hle: 21 }, cost: 3.4, context: 1000 },
  { id: 'gem-31pro',  provider: 'gemini', family: 'Gemini 3.x', name: 'Gemini 3.1 Pro', date: '2026-02', index: 46.5, key: true, bench: { gpqa: 94, hle: 45, tbench: 74 }, cost: 4.5, speed: 130, context: 1000 },
  { id: 'gem-35fl',   provider: 'gemini', family: 'Gemini 3.x', name: 'Gemini 3.5 Flash', date: '2026-05', index: 50.2, key: true, star: true, bench: { gpqa: 92, hle: 41, tbench: 79 }, cost: 3.4, speed: 163, context: 1000 },
  { id: 'gem-35fl-m', provider: 'gemini', family: 'Gemini 3.x', name: 'Gemini 3.5 Flash (medium)', date: '2026-05', index: 45.4, bench: { gpqa: 92, hle: 40 }, cost: 3.4, speed: 170, context: 1000 },

  // ── Grok (cloud) ──
  { id: 'grok-43',    provider: 'grok', family: 'Grok 4.3', name: 'Grok 4.3', date: '2026-04', index: 37.6, key: true, star: true, bench: { gpqa: 90, hle: 35, tbench: 40 }, cost: 1.6, speed: 167, context: 131 },
  { id: 'grok-43m',   provider: 'grok', family: 'Grok 4.3', name: 'Grok 4.3 (medium)', date: '2026-04', index: 36.0, bench: { gpqa: 89, hle: 28 }, cost: 1.6, speed: 172, context: 131 },
  { id: 'grok-43l',   provider: 'grok', family: 'Grok 4.3', name: 'Grok 4.3 (low)', date: '2026-04', index: 35.4, bench: { gpqa: 84, hle: 17 }, cost: 1.6, speed: 133, context: 131 },
  { id: 'grok-43nr',  provider: 'grok', family: 'Grok 4.3', name: 'Grok 4.3 (non-reasoning)', date: '2026-04', index: 24.8, bench: { gpqa: 66, hle: 6, tbench: 34 }, cost: 1.6, speed: 143, context: 131 },

  // ── Kimi / Moonshot (cloud) ──
  { id: 'kimi-k26',   provider: 'kimi', family: 'Kimi K2.6', name: 'Kimi K2.6', date: '2026-04', index: 42.8, key: true, star: true, size: '1T+ MoE', bench: { gpqa: 91, hle: 36, tbench: 57 }, cost: 1.7, speed: 74, context: 256 },
  { id: 'kimi-k27c',  provider: 'kimi', family: 'Kimi K2.7', name: 'Kimi K2.7 Code', date: '2026-06', index: 41.9, key: true, size: '1T+ MoE', bench: { gpqa: 90, hle: 33, tbench: 67 }, cost: 1.7, speed: 55, context: 256 },
  { id: 'kimi-k26nr', provider: 'kimi', family: 'Kimi K2.6', name: 'Kimi K2.6 (non-reasoning)', date: '2026-04', index: 34.6, size: '1T+ MoE', bench: { gpqa: 79, hle: 18 }, cost: 1.7, speed: 74, context: 256 },

  // ── Qwen (open) ──
  { id: 'qwen-next-i', provider: 'qwen', family: 'Qwen3',     name: 'Qwen3 Next 80B', date: '2025-09', index: 13.7, local: true, size: '80B A3B', bench: { gpqa: 74, hle: 7 }, cost: 0.9, speed: 182, context: 262 },
  { id: 'qwen-next-r', provider: 'qwen', family: 'Qwen3',     name: 'Qwen3 Next 80B (reasoning)', date: '2025-09', index: 19.8, key: true, local: true, size: '80B A3B', bench: { gpqa: 76, hle: 12 }, cost: 1.9, speed: 181, context: 262 },
  { id: 'qwen-35-2b',  provider: 'qwen', family: 'Qwen3.5',   name: 'Qwen3.5 2B',    date: '2026-03', index: 10.2, local: true, size: '2B', bench: { gpqa: 46, hle: 2, tbench: 28 }, cost: 0, speed: 46, context: 262 },
  { id: 'qwen-35-4b',  provider: 'qwen', family: 'Qwen3.5',   name: 'Qwen3.5 4B',    date: '2026-03', index: 20.1, local: true, size: '4B', bench: { gpqa: 77, hle: 8, tbench: 26 }, cost: 0.1, speed: 43, context: 262 },
  { id: 'qwen-coder',  provider: 'qwen', family: 'Qwen3',     name: 'Qwen3 Coder Next', date: '2026-02', index: 21.2, size: 'coder', bench: { gpqa: 74, hle: 9 }, cost: 0.6, speed: 121, context: 256 },
  { id: 'qwen-35-9b',  provider: 'qwen', family: 'Qwen3.5',   name: 'Qwen3.5 9B',    date: '2026-03', index: 25, key: true, local: true, size: '9B', bench: { gpqa: 81, hle: 13, tbench: 29 }, cost: 0.1, speed: 59, context: 262 },
  { id: 'qwen-35-122', provider: 'qwen', family: 'Qwen3.5',   name: 'Qwen3.5 122B',  date: '2026-02', index: 32.3, key: true, local: true, size: '122B A10B', bench: { gpqa: 86, hle: 23, tbench: 48 }, cost: 1.1, speed: 141, context: 262 },
  { id: 'qwen-35-397', provider: 'qwen', family: 'Qwen3.5',   name: 'Qwen3.5 397B',  date: '2026-02', index: 33.7, local: true, size: '397B A17B', bench: { gpqa: 89, hle: 27, tbench: 51 }, cost: 1.4, speed: 50, context: 262 },
  { id: 'qwen-36-35',  provider: 'qwen', family: 'Qwen3.6',   name: 'Qwen3.6 35B',   date: '2026-04', index: 31.6, key: true, local: true, size: '35B A3B', bench: { gpqa: 84, hle: 20, tbench: 45 }, cost: 0.6, speed: 178, context: 262 },
  { id: 'qwen-36-27',  provider: 'qwen', family: 'Qwen3.6',   name: 'Qwen3.6 27B',   date: '2026-04', index: 37.1, key: true, star: true, local: true, size: '27B · 32GB VRAM', bench: { gpqa: 84, hle: 22, tbench: 61 }, cost: 1.4, speed: 62, context: 262 },

  // ── Meta / Llama (open) ──
  { id: 'nem-3-ultra', provider: 'meta', family: 'Nemotron 3', name: 'Nemotron 3 Ultra', date: '2026-06', index: 37.8, key: true, star: true, size: '550B A55B', bench: { gpqa: 87, hle: 27, tbench: 54 }, cost: 1.2, speed: 153, context: 262 },
  { id: 'nem-3-super', provider: 'meta', family: 'Nemotron 3', name: 'Nemotron 3 Super', date: '2026-03', index: 25.4, key: true, local: true, size: '120B A12B', bench: { gpqa: 80, hle: 19, tbench: 39 }, cost: 0.4, speed: 245, context: 1000 },
  { id: 'nem-3-nano', provider: 'meta', family: 'Nemotron 3', name: 'Nemotron 3 Nano', date: '2025-12', index: 17.5, local: true, bench: { gpqa: 76, hle: 10 }, cost: 0.1, speed: 104, context: 1000 },
  { id: 'llama-4-scout',provider: 'meta', family: 'Llama 4', name: 'Llama 4 Scout',    date: '2025-04', index: 10, local: true, size: '109B MoE', bench: { gpqa: 59, hle: 4, tbench: 4 }, cost: 0.3, speed: 108, context: 10000 },
  { id: 'llama-nso-49', provider: 'meta', family: 'Llama 4', name: 'Nemotron Super 49B', date: '2025-07', index: 12.4, local: true, size: '49B', bench: { gpqa: 75, hle: 7 }, cost: 0.2, speed: 51, context: 128 },
  { id: 'llama-4-mav',  provider: 'meta', family: 'Llama 4', name: 'Llama 4 Maverick', date: '2025-04', index: 14.3, key: true, size: '400B MoE', bench: { gpqa: 67, hle: 5, tbench: 8 }, cost: 0.5, speed: 114, context: 1000 },
  { id: 'llama-33-70',  provider: 'meta', family: 'Llama 3', name: 'Llama 3.3 70B',    date: '2024-12', index: 8.6, key: true, local: true, size: '70B', bench: { gpqa: 50, hle: 4 }, cost: 0.6, speed: 92, context: 128 },
  { id: 'llama-31-405', provider: 'meta', family: 'Llama 3', name: 'Llama 3.1 405B',   date: '2024-07', index: 8.5, key: true, size: '405B', bench: { gpqa: 52, hle: 4 }, cost: 3.7, speed: 66, context: 128 },

  // ── Gemma (open) ──
  { id: 'gemma-3-12b', provider: 'gemma', family: 'Gemma 3', name: 'Gemma 3 12B',  date: '2025-03', index: 11.2, key: true, local: true, size: '12B', bench: { gpqa: 44, hle: 3 }, cost: 0, speed: 45, context: 128 },
  { id: 'gemma-3-27b', provider: 'gemma', family: 'Gemma 3', name: 'Gemma 3 27B',  date: '2025-03', index: 14.3, key: true, local: true, size: '27B', bench: { gpqa: 55, hle: 4 }, cost: 0, speed: 22, context: 128 },
  { id: 'gemma-4-e2b', provider: 'gemma', family: 'Gemma 4', name: 'Gemma 4 E2B',  date: '2026-04', index: 9.3,  local: true, size: '2B', bench: { gpqa: 43, hle: 5 }, cost: 0, speed: 68, context: 128 },
  { id: 'gemma-4-e4b', provider: 'gemma', family: 'Gemma 4', name: 'Gemma 4 E4B',  date: '2026-04', index: 12.5, key: true, local: true, size: '4B', bench: { gpqa: 58, hle: 4 }, cost: 0, speed: 54, context: 128 },
  { id: 'gemma-4-12b', provider: 'gemma', family: 'Gemma 4', name: 'Gemma 4 12B',  date: '2026-06', index: 22,   key: true, local: true, size: '12B', bench: { gpqa: 75, hle: 15 }, cost: 0.2, speed: 123, context: 256 },
  { id: 'gemma-4-26b', provider: 'gemma', family: 'Gemma 4', name: 'Gemma 4 26B',  date: '2026-04', index: 25.7, local: true, size: '26B A4B', bench: { gpqa: 79, hle: 18, tbench: 39 }, cost: 0.2, speed: 30, context: 256 },
  { id: 'gemma-4-31b', provider: 'gemma', family: 'Gemma 4', name: 'Gemma 4 31B',  date: '2026-04', index: 29.4, key: true, star: true, local: true, size: '31B', bench: { gpqa: 86, hle: 23, tbench: 43 }, cost: 0, speed: 35, context: 256 },

  // ── Mistral (open weights + cloud) ──
  { id: 'mist-3-8b',   provider: 'mistral', family: 'Ministral 3',  name: 'Ministral 3 8B',  date: '2025-12', index: 8.9, local: true, size: '8B', bench: { gpqa: 47, hle: 4, tbench: 7 }, cost: 0.1, speed: 122, context: 256 },
  { id: 'mist-3-14b',  provider: 'mistral', family: 'Ministral 3',  name: 'Ministral 3 14B', date: '2025-12', index: 10, key: true, local: true, size: '14B', bench: { gpqa: 57, hle: 5, tbench: 10 }, cost: 0.2, speed: 99, context: 256 },
  { id: 'mist-small4', provider: 'mistral', family: 'Mistral Small', name: 'Mistral Small 4', date: '2026-03', index: 20.8, key: true, local: true, size: '24B', bench: { gpqa: 77, hle: 9 }, cost: 0.3, speed: 177, context: 256 },
  { id: 'mist-med35',  provider: 'mistral', family: 'Mistral Medium', name: 'Mistral Medium 3.5', date: '2026-04', index: 29.9, key: true, star: true, bench: { gpqa: 75, hle: 13, tbench: 51 }, cost: 3.0, speed: 160, context: 256 },

  // ── DeepSeek (open) ──
  { id: 'ds-v3',       provider: 'deepseek', family: 'DeepSeek V3', name: 'DeepSeek V3',              date: '2025-01', index: 24.2, key: true, bench: { gpqa: 65, hle: 9, tbench: 35 }, cost: 0.3, speed: 115, context: 128 },
  { id: 'ds-v4-pr-nr', provider: 'deepseek', family: 'DeepSeek V4', name: 'DeepSeek V4 Pro', date: '2026-04', index: 31.2, size: '671B MoE', bench: { gpqa: 72, hle: 8 }, cost: 0.5, speed: 98, context: 1000 },
  { id: 'ds-v4-fl-hi', provider: 'deepseek', family: 'DeepSeek V4', name: 'DeepSeek V4 Flash (high)', date: '2026-04', index: 37.4, size: 'Flash', bench: { gpqa: 87, hle: 28 }, cost: 0.2, speed: 95, context: 1000 },
  { id: 'ds-v4-fl',    provider: 'deepseek', family: 'DeepSeek V4', name: 'DeepSeek V4 Flash', date: '2026-04', index: 40.3, key: true, size: 'Flash', bench: { gpqa: 89, hle: 32, tbench: 62 }, cost: 0.2, speed: 110, context: 1000 },
  { id: 'ds-v4-pr-hi', provider: 'deepseek', family: 'DeepSeek V4', name: 'DeepSeek V4 Pro (high)', date: '2026-04', index: 40.8, key: true, size: '671B MoE', bench: { gpqa: 91, hle: 34 }, cost: 0.5, speed: 97, context: 1000 },
  { id: 'ds-v4-pro',   provider: 'deepseek', family: 'DeepSeek V4', name: 'DeepSeek V4 Pro', date: '2026-04', index: 44.3, key: true, star: true, size: '671B MoE', bench: { gpqa: 89, hle: 36, tbench: 64 }, cost: 0.5, speed: 99, context: 1000 },

  // ── GLM / Zhipu (open) ──
  { id: 'glm-51-nr', provider: 'glm', family: 'GLM-5', name: 'GLM-5.1 (non-reasoning)', date: '2026-04', index: 35.4, bench: { gpqa: 84, hle: 26 }, cost: 2.1, speed: 101, context: 200 },
  { id: 'glm-51',    provider: 'glm', family: 'GLM-5', name: 'GLM-5.1', date: '2026-04', index: 40.2, key: true, bench: { gpqa: 87, hle: 28, tbench: 62 }, cost: 2.1, speed: 101, context: 200 },
  { id: 'glm-52',    provider: 'glm', family: 'GLM-5', name: 'GLM-5.2', date: '2026-06', index: 51.1, key: true, star: true, bench: { gpqa: 89, hle: 40, tbench: 78 }, cost: 2.1, speed: 100, context: 1000 },
];

/* ── Derived helpers ───────────────────────────────────────── */
export const MODEL_BY_ID: Record<string, ModelPoint> =
  Object.fromEntries(MODELS.map(m => [m.id, m]));

export const byDate = (a: ModelPoint, b: ModelPoint) =>
  months(a.date) - months(b.date) || a.index - b.index;

export const modelsOfProvider = (id: ProviderId) =>
  MODELS.filter(m => m.provider === id).sort(byDate);

export const keyModelsOfProvider = (id: ProviderId) =>
  modelsOfProvider(id).filter(m => m.key);

export const starOfProvider = (id: ProviderId) =>
  MODELS.find(m => m.provider === id && m.star) ?? keyModelsOfProvider(id).slice(-1)[0];

/** provider -> ordered list of its families (first appearance order) */
export const familiesOfProvider = (id: ProviderId): string[] => {
  const seen: string[] = [];
  for (const m of modelsOfProvider(id)) if (!seen.includes(m.family)) seen.push(m.family);
  return seen;
};

export const TOTAL_MODELS = MODELS.length;

/* ── Unified lookup for comparison: resolves a model id or a frontier name ── */
export const FRONTIER_BY_NAME: Record<string, FrontierPoint> =
  Object.fromEntries(FRONTIER.map(f => [f.name, f]));

export interface CompareItem {
  id: string;
  name: string;
  date: string;
  index: number;
  provider: string;       // display label (lab name or maker)
  providerColor: string;  // gold for frontier, provider color for models
  bench?: Bench;
  cost?: number;
  speed?: number;
  context?: number;
  size?: string;
  cloud: boolean;         // frontier = cloud
}

/** Returns only the best model per release-month per provider → smooth polyline without zigzag. */
export const perMonthLine = (ms: ModelPoint[]): ModelPoint[] => {
  const best = new Map<string, ModelPoint>();
  for (const m of ms) {
    const prev = best.get(m.date);
    if (!prev || m.index > prev.index) best.set(m.date, m);
  }
  return [...best.values()].sort(byDate);
};

/** Open-weight providers only (excludes cloud-only providers). */
export const OPEN_PROVIDERS = PROVIDERS.filter(p => p.deployMode !== 'cloud');

/** Default model IDs for "Local vs Frontier" mode (consumer GPU, ≤32 GB VRAM). */
export const DEFAULT_LOCAL_IDS: string[] = [
  'llama-33-70',  // Meta 8.6 · 2024-12 — historical anchor
  'gemma-3-27b',  // Gemma 14.3 · 2025-03 — early local Gemma
  'qwen-next-r',  // Qwen 19.8 · 2025-09 — Qwen3 Next reasoning
  'mist-3-14b',   // Mistral 10 · 2025-12 — early local Mistral
  'qwen-35-9b',   // Qwen 25 · 2026-03 — runs anywhere
  'nem-3-super',  // Meta 25.4 · 2026-03
  'mist-small4',  // Mistral 20.8 · 2026-03
  'gemma-4-31b',  // Gemma 29.4★ · 2026-04
  'qwen-36-27',   // Qwen 37.1★ · 2026-04 — headline local model
];

/** Default model IDs for "Open Weight vs Frontier" mode (full open landscape). */
export const DEFAULT_OPEN_IDS: string[] = [
  'llama-33-70',  // Meta 8.6 · 2024-12 — historical anchor
  'ds-v3',        // DeepSeek 24.2 · 2025-01 — DeepSeek V3 milestone
  'gemma-3-27b',  // Gemma 14.3 · 2025-03 — early Gemma
  'qwen-next-r',  // Qwen 19.8 · 2025-09 — early Qwen reasoning
  'mist-3-14b',   // Mistral 10 · 2025-12 — early Mistral
  'qwen-35-122',  // Qwen 32.3 · 2026-02
  'nem-3-super',  // Meta 25.4 · 2026-03
  'mist-small4',  // Mistral 20.8 · 2026-03 — intermediate step
  'gemma-4-31b',  // Gemma 29.4★ · 2026-04
  'mist-med35',   // Mistral 29.9★ · 2026-04
  'qwen-36-27',   // Qwen 37.1★ · 2026-04
  'kimi-k26',     // Kimi 42.8★ · 2026-04 — open-weight 1T MoE
  'kimi-k27c',    // Kimi 41.9 · 2026-06 — Code variant
  'glm-51',       // GLM 40.2 · 2026-04
  'ds-v4-fl',     // DeepSeek 40.3 · 2026-04 — Flash (open-weight)
  'ds-v4-pro',    // DeepSeek 44.3★ · 2026-04
  'nem-3-ultra',  // Meta 37.8★ · 2026-06
  'glm-52',       // GLM 51.1★ · 2026-06
];

/* ── Hardware tiers for ROI / Breakeven analysis ───────────── */
export interface HardwareSpec {
  label: string;
  gpu: string;
  vram: string;
  gpuPriceUsd: number;
  monthlyPowerUsd: number;
  monthlyMaintenanceUsd: number;
}

export const HARDWARE_TIERS: Record<string, HardwareSpec> = {
  'rtx-4070ti': { label: 'RTX 4070 Ti · 16 GB', gpu: 'GeForce RTX 4070 Ti', vram: '16 GB VRAM', gpuPriceUsd: 800,  monthlyPowerUsd: 22, monthlyMaintenanceUsd: 4  },
  'rtx-4090':   { label: 'RTX 4090 · 24 GB',    gpu: 'GeForce RTX 4090',    vram: '24 GB VRAM', gpuPriceUsd: 1900, monthlyPowerUsd: 49, monthlyMaintenanceUsd: 8  },
  'rtx-a6000':  { label: 'RTX A6000 · 48 GB',   gpu: 'NVIDIA RTX A6000',    vram: '48 GB VRAM', gpuPriceUsd: 4500, monthlyPowerUsd: 32, monthlyMaintenanceUsd: 20 },
  'a100-80':    { label: 'A100 SXM · 80 GB',     gpu: 'NVIDIA A100 SXM',     vram: '80 GB VRAM',      gpuPriceUsd: 15000, monthlyPowerUsd: 61,  monthlyMaintenanceUsd: 60  },
  '2xa100':     { label: '2× A100 · 160 GB',     gpu: '2× NVIDIA A100 SXM',  vram: '2× 80 GB VRAM',   gpuPriceUsd: 30000, monthlyPowerUsd: 122, monthlyMaintenanceUsd: 120 },
};

/** Minimum GPU tier required to run each local model (MoE active-param counts, not total). */
export const MODEL_HW: Record<string, string> = {
  // 16 GB (RTX 4070 Ti) — small dense or MoE ≤4 B active params
  'gemma-4-e2b':  'rtx-4070ti', 'gemma-4-e4b':  'rtx-4070ti',
  'qwen-35-2b':   'rtx-4070ti', 'qwen-35-4b':   'rtx-4070ti', 'qwen-35-9b':   'rtx-4070ti',
  'mist-3-8b':    'rtx-4070ti', 'mist-3-14b':   'rtx-4070ti',
  'gemma-3-12b':  'rtx-4070ti',
  'qwen-next-r':  'rtx-4070ti', 'qwen-next-i':  'rtx-4070ti',  // 80 B MoE, 3 B active
  'qwen-36-35':   'rtx-4070ti',                                 // 35 B MoE, 3 B active
  'nem-3-nano':   'rtx-4070ti', 'llama-4-scout': 'rtx-4070ti', // MoE, small active
  // 24 GB (RTX 4090) — medium dense or MoE ≤14 B active params
  'gemma-3-27b':  'rtx-4090', 'gemma-4-12b':  'rtx-4090', 'gemma-4-26b':  'rtx-4090',
  'mist-small4':  'rtx-4090', 'qwen-36-27':   'rtx-4090', 'llama-nso-49': 'rtx-4090',
  'nem-3-super':  'rtx-4090', 'qwen-35-122':  'rtx-4090',  // MoE ≤12 B active
  // 48 GB (RTX A6000) — larger dense or MoE ≤20 B active params
  'gemma-4-31b':  'rtx-a6000', 'qwen-35-397':  'rtx-a6000', 'llama-33-70':  'rtx-a6000',
  'ds-v4-fl':     'rtx-a6000',                               // Flash: compact MoE, fits quantized
  // 80 GB server GPU — GLM-5.1 (~70 B dense equiv.)
  'glm-51':       'a100-80',   'glm-51-nr':    'a100-80',
  // 2× 80 GB server — GLM-5.2 (>200 B)
  'glm-52':       '2xa100',
};

export interface CloudOption {
  id: string;
  name: string;
  maker: string;
  costPerM: number;
  color: string;
}

export const ROI_CLOUD_OPTIONS: CloudOption[] = [
  { id: 'gem35fl', name: 'Gemini 3.5 Flash', maker: 'Google',    costPerM: 3.4,  color: '#3b82f6' },
  { id: 'gpt55',   name: 'GPT-5.5',          maker: 'OpenAI',    costPerM: 11.2, color: '#10a37f' },
  { id: 'opus48',  name: 'Claude Opus 4.8',  maker: 'Anthropic', costPerM: 10,   color: '#f0a060' },
  { id: 'grok43',  name: 'Grok 4.3',         maker: 'xAI',       costPerM: 1.6,  color: '#f59e0b' },
  { id: 'dsv4',    name: 'DeepSeek V4 Pro',  maker: 'DeepSeek',  costPerM: 0.5,  color: '#f43f5e' },
];

export const DEFAULT_ROI_CLOUD_IDS = ['gpt55', 'opus48'];
export const DEFAULT_ROI_LOCAL_IDS = ['gemma-4-31b', 'qwen-36-27'];

/** Resolve any comparison id (model id or `f:`-prefixed frontier name) → CompareItem */
export const resolveCompare = (id: string): CompareItem | null => {
  if (id.startsWith('f:')) {
    const name = id.slice(2);
    const f = FRONTIER_BY_NAME[name];
    if (!f) return null;
    return {
      id, name: f.name, date: f.date, index: f.index,
      provider: f.maker, providerColor: '#e8c46a',
      bench: f.bench, cost: f.cost, speed: f.speed, context: f.context,
      cloud: true,
    };
  }
  const m = MODEL_BY_ID[id];
  if (!m) return null;
  const p = PROVIDER_MAP[m.provider];
  return {
    id, name: m.name, date: m.date, index: m.index,
    provider: p.label, providerColor: p.color,
    bench: m.bench, cost: m.cost, speed: m.speed, context: m.context, size: m.size,
    cloud: p.deployMode === 'cloud',
  };
};
